import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@fortawesome/free-solid-svg-icons";

// Matches the redesigned ConsensusQuestion.js header style: aligned uppercase
// micro-labels, monospace IDs, status dot, copy icons with flexShrink: 0.

export const headerLabelStyle = {
  flexShrink: 0,
  width: "120px",
  textTransform: "uppercase",
  fontSize: "0.68em",
  fontWeight: 600,
  letterSpacing: "0.05em",
  color: "#8a8a8a",
};

export const monoValueStyle = {
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
  fontSize: "0.9em",
  wordBreak: "break-all",
};

export const CopyIconButton = ({ value, title = "Copy" }) => {
  const handleCopy = (e) => {
    e.stopPropagation();
    if (value === null || value === undefined) return;
    navigator.clipboard.writeText(String(value)).then(() => {});
  };
  return (
    <FontAwesomeIcon
      icon={faCopy}
      title={title}
      onClick={handleCopy}
      style={{ cursor: "pointer", marginLeft: "6px", color: "#666", flexShrink: 0 }}
    />
  );
};

export const HeaderRow = ({ label, children }) => (
  <div style={{ display: "flex", alignItems: "baseline", gap: "10px" }}>
    <span style={headerLabelStyle}>{label}</span>
    <span
      style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", minWidth: 0 }}
    >
      {children}
    </span>
  </div>
);

export const StatusPill = ({ status }) => {
  const isActive = String(status).toLowerCase() === "active";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        fontSize: "0.8em",
        fontWeight: 600,
        letterSpacing: "0.03em",
        color: isActive ? "#1a7a3a" : "#888",
      }}
    >
      <span
        style={{
          width: "7px",
          height: "7px",
          borderRadius: "50%",
          background: isActive ? "#22a356" : "#bbb",
        }}
      />
      {String(status || "—").toUpperCase()}
    </span>
  );
};

// Renders a nullable boolean (word_problem / illustration / equation_centered)
// as Yes / No / No data.
export const TriBoolValue = ({ value }) => {
  if (value === true) return <span>Yes</span>;
  if (value === false) return <span>No</span>;
  return <span style={{ color: "#aaa" }}>No data</span>;
};

export const StandardsBadges = ({ standards }) => {
  if (!standards || standards.length === 0) return <span style={{ color: "#aaa" }}>—</span>;
  return (
    <span style={{ display: "inline-flex", flexWrap: "wrap", gap: "4px" }}>
      {standards.map((s) => (
        <span
          key={s}
          style={{
            background: "#e6f0ff",
            color: "#1a3a8a",
            borderRadius: "10px",
            padding: "2px 8px",
            fontSize: "0.85em",
            fontWeight: 500,
          }}
        >
          {s}
        </span>
      ))}
    </span>
  );
};

export const SectionCard = ({ title, children, style }) => (
  <div
    style={{
      padding: "14px 16px",
      border: "1px solid #e2e2e2",
      borderRadius: "8px",
      background: "#fff",
      ...style,
    }}
  >
    {title && (
      <div
        style={{
          textTransform: "uppercase",
          fontSize: "0.7em",
          fontWeight: 700,
          letterSpacing: "0.06em",
          color: "#8a8a8a",
          marginBottom: "10px",
        }}
      >
        {title}
      </div>
    )}
    {children}
  </div>
);

// Strip stored HTML/LaTeX prompt to a clean preview line.
export const stripHtmlPreview = (html, max = 160) => {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  const text = (tmp.textContent || tmp.innerText || "").replace(/\s+/g, " ").trim();
  return text.length > max ? `${text.slice(0, max)}…` : text;
};

export const fmtDate = (value) => {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString();
  } catch (e) {
    return String(value);
  }
};
